/** CJ B5 5.0.6 — progressive enhancement ONLY.
 * Every trigger, card and overview is already in server-rendered Liquid.
 * Native details handles touch/keyboard, including when this script fails.
 * No HTML cloning, fetch, imports, perpetual observers or title matching.
 */
(() => {
  if (customElements.get('cj-mega-navigation')) return;
  class CJMegaNavigation extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('click', this.onClick, options);
      this.addEventListener('toggle', this.onToggle, { ...options, capture: true });
      this.addEventListener('pointerover', this.onOver, options);
      this.addEventListener('pointerout', this.onOut, options);
      this.addEventListener('keydown', this.onKey, options);
      this.addEventListener('keyup', this.onKeyUp, options);
      this.addEventListener('focusout', this.onFocusOut, options);
      document.addEventListener('pointerdown', this.onOutside, options);
      document.addEventListener('keydown', this.onDocumentKey, options);
      document.addEventListener('shopify:block:select', this.onEditorSelect, options);
      window.addEventListener('resize', this.onResize, { ...options, passive: true });
      window.addEventListener('pageshow', this.closeAll, options);
      window.addEventListener('scroll', this.measure, { ...options, passive: true });
      this.syncLayout();
      this.measure();
    }
    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
    }
    get desktop() { return this.dataset.cj6View === 'desktop'; }
    get groups() { return [...this.querySelectorAll('[data-cj6-group]')]; }
    groupFor(target) {
      const group = target instanceof Element ? target.closest('[data-cj6-group]') : null;
      return group && this.contains(group) ? group : null;
    }
    show(group) {
      clearTimeout(this.closeTimer);
      if (!group) return;
      this.groups.forEach(other => { if (other !== group) other.open = false; });
      group.open = true;
      this.measure();
    }
    closeAll = () => { clearTimeout(this.closeTimer); this.hoverOnly = null; this.groups.forEach(group => { group.open = false; }); };
    onClick = event => {
      const summary = event.target.closest?.('.cj6-trigger');
      if (!summary || !this.contains(summary)) return;
      // Leave the actual toggle to HTML. Close siblings synchronously (Safari).
      const group = this.groupFor(summary);
      this.groups.forEach(other => { if (other !== group) other.open = false; });
      clearTimeout(this.closeTimer);
      if (this.desktop && event.detail > 0 && group?.open && this.hoverOnly === group) {
        event.preventDefault(); // first pointer click keeps the hover-open menu open
        this.hoverOnly = null;
      }
      this.measure();
    };
    onToggle = event => {
      const group = event.target;
      if (!group.matches?.('[data-cj6-group]')) return;
      if (group.open) this.groups.forEach(other => { if (other !== group) other.open = false; });
      this.measure();
    };
    onOver = event => {
      if (!this.desktop || event.pointerType === 'touch' || !matchMedia('(hover: hover)').matches) return;
      const group = this.groupFor(event.target);
      clearTimeout(this.closeTimer);
      if (group) { if (!group.open) this.hoverOnly = group; this.show(group); }
      else if (event.target.closest?.('.cj6-direct-link')) this.closeAll();
    };
    onOut = event => {
      if (!this.desktop || event.pointerType === 'touch') return;
      const group = this.groupFor(event.target);
      if (group?.contains(event.relatedTarget)) return;
      if (this.contains(event.relatedTarget)) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.contains(document.activeElement)) this.closeAll();
      }, 180);
    };
    onOutside = event => { if (this.desktop && !this.contains(event.target)) this.closeAll(); };
    onFocusOut = event => {
      if (!this.desktop || this.contains(event.relatedTarget)) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => { if (!this.matches(':hover') && !this.contains(document.activeElement)) this.closeAll(); }, 0);
    };
    onKey = event => {
      const group = this.groupFor(event.target);
      if (event.key === 'Escape' && group?.open) {
        event.preventDefault(); event.stopPropagation();
        group.open = false;
        this.consumedEscape = true;
        group.querySelector(':scope > summary')?.focus({ preventScroll: true });
      } else if (event.key === 'ArrowDown' && event.target.matches('.cj6-trigger, .cj6-trigger *')) {
        event.preventDefault(); this.show(group);
        group?.querySelector('.cj6-panel a[href]')?.focus();
      }
    };
    onKeyUp = event => {
      // Do not let HeaderDrawer interpret a consumed Escape as closing the shell.
      if (event.key === 'Escape' && this.consumedEscape) {
        event.preventDefault(); event.stopPropagation(); this.consumedEscape = false;
      }
    };
    onDocumentKey = event => {
      if (event.key === 'Escape' && this.desktop && !event.defaultPrevented && !this.contains(event.target)) this.closeAll();
    };
    // Keep the native header's data attribute consistent with the CSS breakpoint.
    // Runs inside the existing component lifecycle: no second global listener.
    syncLayout() {
      if (!this.desktop) return;
      const header = this.closest('header-component');
      if (!header) return;
      const style = matchMedia('(min-width: 990px)').matches ? 'menu' : 'drawer';
      if (header.dataset.menuStyle !== style) header.dataset.menuStyle = style;
      if (style === 'menu') {
        const drawer = header.querySelector('header-drawer');
        if (drawer?.isOpen) drawer.close?.();
      }
    }
    onResize = () => { this.closeAll(); this.syncLayout(); this.measure(); };
    measure = () => {
      if (!this.desktop || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const header = this.closest('header-component');
        if (!header) return;
        header.style.setProperty('--cj6-panel-room', `${Math.max(120, window.innerHeight - header.getBoundingClientRect().bottom - 12)}px`);
      });
    };
    onEditorSelect = event => {
      if (!window.Shopify?.designMode) return;
      const selected = event.target.closest?.('[data-cj6-block]');
      if (!selected) return;
      const showMobile = window.innerWidth < 990 || this.closest('header-component')?.dataset.menuStyle === 'drawer';
      if (this.desktop === showMobile) return;
      const group = this.groups.find(item => item.dataset.cj6Block === selected.dataset.cj6Block);
      if (!group) return;
      if (!this.desktop) {
        const drawer = this.closest('header-drawer');
        const rootDetails = drawer?.querySelector(':scope > details');
        if (rootDetails) { rootDetails.open = true; drawer.open?.(); }
      }
      this.show(group);
    };
  }
  customElements.define('cj-mega-navigation', CJMegaNavigation);
})();
